import { useEffect, useRef, useState } from 'react';
import { api } from './api';

export type LiveMessage =
  | { type: 'hello'; kind: string; serverTime: string }
  | { type: 'telemetry'; deviceId: string; devEui: string; name?: string; time: string;
      values: Record<string, unknown>; rssi?: number; snr?: number; decoderVerified?: boolean }
  | { type: 'device:discovered'; device: any }
  | { type: 'alarm:raised'; alarm: any; deviceId: string | null }
  | { type: 'alarm:cleared'; alarmId: string; alarmKey: string; deviceId: string | null }
  | { type: 'pong'; serverTime: string };

type Options = {
  /** Public share pages connect with a share token instead of a session. */
  shareToken?: string;
  deviceIds?: string[];
  enabled?: boolean;
  onMessage?: (message: LiveMessage) => void;
};

const MAX_BACKOFF_MS = 30_000;

/**
 * Live updates.
 *
 * Reconnects with exponential backoff and jitter — without jitter, every
 * dashboard in a building reconnects in lockstep after a restart and stampedes
 * the server. The connection state is surfaced honestly rather than pretending
 * a stalled socket is live.
 */
export function useLive({ shareToken, deviceIds, enabled = true, onMessage }: Options = {}) {
  const [connected, setConnected] = useState(false);
  const [lastMessageAt, setLastMessageAt] = useState<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  // Kept in a ref so a changing callback does not tear down the socket, and
  // synced in an effect rather than mutated during render.
  const handlerRef = useRef(onMessage);
  useEffect(() => { handlerRef.current = onMessage; }, [onMessage]);

  const deviceKey = deviceIds?.slice().sort().join(',') ?? '';

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    async function connect() {
      if (closed) return;
      try {
        const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        let url: string;

        if (shareToken) {
          url = `${protocol}//${location.host}/ws/share?token=${encodeURIComponent(shareToken)}`;
        } else {
          // A 30-second ticket, so no durable credential ever appears in a URL.
          const { ticket } = await api.post<{ ticket: string }>('/api/auth/ws-ticket');
          url = `${protocol}//${location.host}/ws?ticket=${encodeURIComponent(ticket)}`;
        }

        const socket = new WebSocket(url);
        socketRef.current = socket;

        socket.onopen = () => {
          attempt = 0;
          setConnected(true);
          if (deviceKey) {
            socket.send(JSON.stringify({ type: 'subscribe', deviceIds: deviceKey.split(',') }));
          }
        };

        socket.onmessage = (event) => {
          setLastMessageAt(Date.now());
          try {
            handlerRef.current?.(JSON.parse(event.data));
          } catch { /* ignore malformed frames */ }
        };

        socket.onclose = () => {
          setConnected(false);
          socketRef.current = null;
          if (closed) return;
          attempt++;
          const backoff = Math.min(1000 * 2 ** (attempt - 1), MAX_BACKOFF_MS);
          const jitter = backoff * (0.5 + Math.random() * 0.5);
          retryTimer = setTimeout(connect, jitter);
        };

        socket.onerror = () => socket.close();
      } catch {
        if (closed) return;
        attempt++;
        retryTimer = setTimeout(connect, Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS));
      }
    }

    void connect();
    return () => {
      closed = true;
      clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, shareToken, deviceKey]);

  return { connected, lastMessageAt };
}
